import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, OnInit, Signal, effect, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../core/models/address.model';
import { AppointmentType } from '../../core/models/appointment.model';
import { computeGrandTotal, LeadLineItem, parseLeadServices, PreferredContactMethod } from '../../core/models/lead.model';
import { Service } from '../../core/models/service.model';
import { parseDefaultTagIds } from '../../core/models/work-context.model';
import { AddressService } from '../../core/services/address.service';
import { AppointmentService } from '../../core/services/appointment.service';
import { AttachmentService } from '../../core/services/attachment.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { InteractionService } from '../../core/services/interaction.service';
import { InteractionTypeService } from '../../core/services/interaction-type.service';
import { LeadService } from '../../core/services/lead.service';
import { LoggerService } from '../../core/services/logger.service';
import { NoopSchedulingProvider } from '../../core/services/scheduling/noop-scheduling-provider';
import { ServiceService } from '../../core/services/service.service';
import { TagService } from '../../core/services/tag.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { WorkContextService } from '../../core/services/work-context.service';
import { ACTIVE_WORK_CONTEXT_KEY } from '../my-day/active-work-context';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { DncConfirmDialogComponent } from '../../shared/components/dnc-confirm-dialog/dnc-confirm-dialog.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { ActionSheetHost } from '../map/action-sheet/action-sheet.component';
import { LeadWizardStepIndicatorComponent } from './lead-wizard-step-indicator.component';

interface ContactModel {
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  preferred_contact_method: PreferredContactMethod;
}

interface InterestedModel {
  service_quantities: Record<string, number>;
  service_selected: Record<string, boolean>;
  currency: string;
}

interface DetailsModel {
  notes: string;
  photos: string[];
}

interface ScheduleModel {
  on: boolean;
  appointment_type: AppointmentType;
  start: string; // datetime-local string; converted to epoch ms on save
  end: string;
  notes: string;
}

const STEP_KEYS = ['contact', 'interested', 'details', 'schedule', 'review'] as const;

const PHONE_PATTERN = '\\+?[0-9()\\-.\\s]{7,20}';
const EMAIL_PATTERN = '[^\\s@]+@[^\\s@]+\\.[^\\s@]+';

/**
 * The full Lead Wizard — the only path that ever writes outcome_kind: 'lead'.
 * Reached from the interaction screen's "This is a Lead" tile as a sibling
 * route (`.../interactions/new/lead` or `.../interactions/:interactionId/lead`
 * -- see app.routes.ts), not a separately-addressed page. One component, five
 * FormGroup/model pairs (one per step, not one shared FormGroup — see the
 * Record Interaction redesign plan's wizard-architecture section), sharing a
 * responsive shell with LeadWizardStepIndicatorComponent.
 */
@Component({
  selector: 'app-lead-wizard',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    DecimalPipe,
    ButtonComponent,
    DncConfirmDialogComponent,
    IconComponent,
    SpinnerComponent,
    LeadWizardStepIndicatorComponent,
    TranslatePipe,
  ],
  templateUrl: './lead-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lead-wizard.component.css',
})
export class LeadWizardComponent implements OnInit, ActionSheetHost {
  private addressService = inject(AddressService);
  private appointmentService = inject(AppointmentService);
  private attachmentService = inject(AttachmentService);
  private interactionService = inject(InteractionService);
  private interactionTypeService = inject(InteractionTypeService);
  private leadService = inject(LeadService);
  private schedulingProvider = inject(NoopSchedulingProvider);
  private serviceService = inject(ServiceService);
  private tagService = inject(TagService);
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);
  private workContextService = inject(WorkContextService);
  private breadcrumbService = inject(BreadcrumbService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private injector = inject(Injector);
  private log = inject(LoggerService).getLogger('lead-wizard');

  address!: Signal<Address | null>;
  addressLoading!: Signal<boolean>;

  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  savedFinal = signal(false);

  currentStep = signal<0 | 1 | 2 | 3 | 4>(0);
  stepLabels = signal<string[]>([]);
  grandTotal = signal(0);

  contactForm = new FormGroup({});
  contactModel: ContactModel = { contact_name: '', contact_phone: '', contact_email: '', preferred_contact_method: '' };
  contactFields: FormlyFieldConfig[] = [];
  /** True once neither `contact_phone` nor `contact_email` has a value -- kept in sync via
   *  `contactForm.valueChanges` (see ngOnInit) rather than a form-level validator, since Formly's
   *  `clearControl()` wipes any validator attached directly to the root FormGroup on first bind. */
  contactMethodMissing = signal(true);

  interestedForm = new FormGroup({});
  interestedModel: InterestedModel = { service_quantities: {}, service_selected: {}, currency: 'CAD' };
  interestedFields: FormlyFieldConfig[] = [];

  detailsForm = new FormGroup({});
  detailsModel: DetailsModel = { notes: '', photos: [] };
  detailsFields: FormlyFieldConfig[] = [];

  scheduleForm = new FormGroup({});
  scheduleModel: ScheduleModel = { on: false, appointment_type: 'estimate', start: '', end: '', notes: '' };
  scheduleFields: FormlyFieldConfig[] = [];

  private services: Service[] = [];
  private interactionType = 'door_knock';
  private tagIds: string[] = [];
  private addressId = '';
  private interactionId: string | null = null;
  private existingLeadId: string | null = null;
  private existingAppointmentId: string | null = null;

  /** Non-null while the address is flagged do-not-contact and the rep hasn't
   *  confirmed past the gate yet -- see the Record Interaction redesign plan's
   *  confirmed decision #5 (address-level, outcome-independent confirm). */
  pendingDncConfirm = signal(false);
  private dncGateConfirmed = false;

  async ngOnInit(): Promise<void> {
    this.stepLabels.set(STEP_KEYS.map((k) => this.translate.translate(`interactions.wizard.steps.${k}`)));

    // ':id' is the ancestor addresses/:id segment's param (available here via
    // paramsInheritanceStrategy: 'always'); 'interactionId' is this route's own
    // segment -- absent on the 'new' sibling, present when editing.
    this.addressId = this.route.snapshot.paramMap.get('id') ?? '';
    this.interactionId = this.route.snapshot.paramMap.get('interactionId');

    this.services = await this.serviceService.getActive();
    const t = (key: string) => this.translate.translate(key);

    this.contactFields = [
      { key: 'contact_name', type: 'text', props: { label: t('interactions.form.lead.contactName'), required: true } },
      {
        key: 'contact_phone',
        type: 'tel',
        props: { label: t('interactions.form.lead.contactPhone'), pattern: PHONE_PATTERN },
      },
      {
        key: 'contact_email',
        type: 'email',
        props: { label: t('interactions.form.lead.contactEmail'), pattern: EMAIL_PATTERN },
      },
      {
        key: 'preferred_contact_method',
        type: 'native-select',
        props: {
          label: t('interactions.form.lead.preferredContact'),
          showLabel: true,
          options: [
            { value: '', label: t('interactions.form.lead.preferredContactNone') },
            { value: 'phone', label: t('interactions.form.lead.contactPhone') },
            { value: 'email', label: t('interactions.form.lead.contactEmail') },
            { value: 'text', label: t('interactions.form.lead.contactText') },
          ],
        },
      },
    ];

    this.buildInterestedFields();

    this.detailsFields = [
      { key: 'notes', type: 'textarea', props: { label: t('interactions.form.lead.notesLabel') } },
      { key: 'photos', type: 'photo', props: { label: t('interactions.form.photosLabel'), allowMultiplePhotos: true } },
    ];

    this.scheduleFields = [
      {
        key: 'on',
        type: 'toggle',
        props: { label: t('interactions.wizard.schedule.toggleLabel'), attributes: { 'data-testid': 'wizard-schedule-toggle' } },
      },
      {
        key: 'appointment_type',
        type: 'native-select',
        props: {
          label: t('interactions.form.appointment.typeLabel'),
          showLabel: true,
          options: [
            { value: 'estimate', label: t('interactions.form.appointment.type.estimate') },
            { value: 'service_call', label: t('interactions.form.appointment.type.serviceCall') },
            { value: 'follow_up', label: t('interactions.form.appointment.type.followUp') },
          ],
        },
        expressions: { hide: (field) => !field.model?.on },
      },
      {
        key: 'start',
        type: 'datetime',
        props: { label: t('interactions.form.appointment.startLabel') },
        expressions: { hide: (field) => !field.model?.on },
      },
      {
        key: 'end',
        type: 'datetime',
        props: { label: t('interactions.form.appointment.endLabel') },
        expressions: { hide: (field) => !field.model?.on },
      },
      {
        key: 'notes',
        type: 'textarea',
        props: { label: t('interactions.form.appointment.notesLabel') },
        expressions: { hide: (field) => !field.model?.on },
      },
    ];

    if (this.interactionId) {
      this.isEditMode.set(true);
      await this.loadForEdit(this.interactionId);
    } else {
      await this.loadAddress(this.addressId);
      await this.applyActiveWorkContextDefaults();
      this.breadcrumbService.setDynamicLabel(t('interactions.form.newTitle'));
    }

    this.interestedForm.valueChanges.subscribe(() => this.recomputeGrandTotal());
    this.contactForm.valueChanges.subscribe(() => this.recomputeContactMethodMissing());
    this.recomputeContactMethodMissing();
    this.loading.set(false);
  }

  private recomputeContactMethodMissing(): void {
    const hasPhone = !!this.contactModel.contact_phone?.trim();
    const hasEmail = !!this.contactModel.contact_email?.trim();
    this.contactMethodMissing.set(!hasPhone && !hasEmail);
  }

  private async loadAddress(id: string): Promise<void> {
    const addressQuery = this.addressService.createQueryById(id);
    this.address = addressQuery.data;
    this.addressLoading = addressQuery.loading;

    effect(
      () => {
        const address = this.address();
        if (address?.do_not_contact && !this.dncGateConfirmed) {
          this.pendingDncConfirm.set(true);
        }
      },
      { injector: this.injector },
    );
  }

  private buildInterestedFields(): void {
    const t = (key: string) => this.translate.translate(key);
    this.interestedModel.service_quantities = {};
    this.interestedModel.service_selected = {};

    const serviceRows: FormlyFieldConfig[] = this.services.map((svc) => {
      this.interestedModel.service_selected[svc.id] = false;
      const priced = svc.default_price != null;
      const checkboxField: FormlyFieldConfig = {
        key: `service_selected.${svc.id}`,
        type: 'checkbox',
        className: 'section-field service-label-field',
        props: { label: priced ? `${svc.label} — $${svc.default_price!.toFixed(2)}` : svc.label },
      };

      if (!priced) {
        return {
          className: 'section-field',
          fieldGroupClassName: 'service-checklist-row flex-responsive',
          fieldGroup: [checkboxField],
        };
      }

      this.interestedModel.service_quantities[svc.id] = 1;
      return {
        className: 'section-field',
        fieldGroupClassName: 'service-checklist-row flex-responsive',
        fieldGroup: [
          checkboxField,
          {
            key: `service_quantities.${svc.id}`,
            type: 'number',
            className: 'section-field service-qty-field',
            props: { label: t('interactions.wizard.interested.qtyLabel'), min: 1 },
            expressions: { hide: (field) => !field.model?.service_selected?.[svc.id] },
          },
        ],
      };
    });

    this.interestedFields = [
      ...serviceRows,
      {
        key: 'currency',
        type: 'native-select',
        props: {
          label: t('interactions.form.lead.currency'),
          showLabel: true,
          options: [
            { value: 'CAD', label: 'CAD' },
            { value: 'USD', label: 'USD' },
          ],
        },
      },
    ];
    this.recomputeGrandTotal();
  }

  private async applyActiveWorkContextDefaults(): Promise<void> {
    const activeId = this.userStorage.getTypedItem<string>(ACTIVE_WORK_CONTEXT_KEY);
    if (!activeId) return;
    const contexts = await this.workContextService.getActive();
    const context = contexts.find((c) => c.id === activeId);
    if (!context) return;

    if (context.default_interaction_type) this.interactionType = context.default_interaction_type;
    this.tagIds = parseDefaultTagIds(context);
  }

  private async loadForEdit(interactionId: string): Promise<void> {
    try {
      const interaction = await this.interactionService.getById(interactionId);
      if (!interaction) {
        this.error.set(this.translate.translate('interactions.form.notFoundError'));
        return;
      }
      this.addressId = interaction.address_id;
      this.interactionType = interaction.interaction_type;
      await this.loadAddress(this.addressId);
      this.breadcrumbService.setDynamicLabel(this.translate.translate('interactions.form.editTitle'));

      this.detailsModel.notes = interaction.notes;

      if (interaction.lead_id) {
        this.existingLeadId = interaction.lead_id;
        const lead = await this.leadService.getById(interaction.lead_id);
        if (lead) {
          this.contactModel = {
            contact_name: lead.contact_name,
            contact_phone: lead.contact_phone,
            contact_email: lead.contact_email,
            preferred_contact_method: lead.preferred_contact_method,
          };
          this.interestedModel.currency = lead.currency;
          for (const item of parseLeadServices(lead)) {
            this.interestedModel.service_selected[item.service_id] = true;
            this.interestedModel.service_quantities[item.service_id] = item.quantity;
          }

          const appointments = await this.appointmentService.getAllForLead(lead.id);
          const appointment = appointments.find((a) => a.status !== 'cancelled') ?? null;
          if (appointment) {
            this.existingAppointmentId = appointment.id;
            this.scheduleModel = {
              on: true,
              appointment_type: appointment.appointment_type,
              start: this.toLocalInput(appointment.scheduled_start),
              end: this.toLocalInput(appointment.scheduled_end),
              notes: appointment.notes,
            };
          }
        }
      }
      this.recomputeGrandTotal();
    } catch (err) {
      this.log.error('Failed to load interaction for edit', err);
      this.error.set(this.translate.translate('interactions.form.loadError'));
    }
  }

  /** Builds the estimate's line items from the current form values — unselected rows are dropped. */
  private buildLineItems(): LeadLineItem[] {
    const items: LeadLineItem[] = [];
    for (const svc of this.services) {
      if (!this.interestedModel.service_selected[svc.id]) continue;
      const quantity = svc.default_price != null ? (this.interestedModel.service_quantities[svc.id] ?? 1) : 1;
      const unitPrice = svc.default_price ?? 0;
      items.push({ service_id: svc.id, label: svc.label, quantity, unit_price: unitPrice, line_total: quantity * unitPrice });
    }
    return items;
  }

  private recomputeGrandTotal(): void {
    this.grandTotal.set(computeGrandTotal(this.buildLineItems()));
  }

  interestedSummary(): { label: string; quantity: number }[] {
    return this.buildLineItems().map((item) => ({ label: item.label, quantity: item.quantity }));
  }

  scheduleSummary(): string {
    if (!this.scheduleModel.on || !this.scheduleModel.start) {
      return this.translate.translate('interactions.wizard.review.noSchedule');
    }
    const d = new Date(this.scheduleModel.start);
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  }

  goToStep(step: number): void {
    if (step >= 0 && step <= 4) this.currentStep.set(step as 0 | 1 | 2 | 3 | 4);
  }

  back(): void {
    if (this.currentStep() > 0) this.currentStep.update((s) => (s - 1) as 0 | 1 | 2 | 3 | 4);
  }

  next(): void {
    if (this.currentStep() === 0 && !this.validateContactStep()) return;
    if (this.currentStep() < 4) this.currentStep.update((s) => (s + 1) as 0 | 1 | 2 | 3 | 4);
  }

  /** Reveals validation messages (required name, phone/email format, "one of phone/email") even if
   *  the user tabbed through without blurring an invalid field, then blocks advancing past them. */
  private validateContactStep(): boolean {
    this.contactForm.markAllAsTouched();
    this.recomputeContactMethodMissing();
    return this.contactForm.valid && !this.contactMethodMissing();
  }

  showContactMethodError(): boolean {
    return this.contactForm.touched && this.contactMethodMissing();
  }

  skipSchedule(): void {
    this.goToStep(4);
  }

  /** Absolute, not relative -- see InteractionComponent's equivalent comment
   *  for why relative '..' navigation from a route like this one is unsafe.
   *
   *  Edit mode cancels straight to the address page rather than the bare
   *  `interactions/:interactionId` route: that route's InteractionComponent
   *  only understands 'no_answer'/'objection' outcomes and falls back to its
   *  legacy-outcome screen for anything else, so landing there for an
   *  already-committed 'lead' interaction showed a confusing "predates the
   *  current outcomes" message instead of cancelling cleanly. Create mode has
   *  no interaction yet, so it still returns to the tile-selection screen. */
  onCancel(): void {
    if (this.isEditMode()) {
      this.router.navigate(['/map/addresses', this.addressId]);
      return;
    }
    this.router.navigate(['/map/addresses', this.addressId, 'interactions', 'new']);
  }

  /** ActionSheetHost -- don't let an outside click or drag-to-dismiss interrupt a submit in flight. */
  canDismissActionSheet(): boolean {
    return !this.saving();
  }

  confirmDncGate(): void {
    this.dncGateConfirmed = true;
    this.pendingDncConfirm.set(false);
  }

  cancelDncGate(): void {
    this.pendingDncConfirm.set(false);
    this.router.navigate(['/map/addresses', this.addressId]);
  }

  async save(): Promise<void> {
    if (this.saving()) return;
    this.saving.set(true);
    this.error.set(null);

    try {
      const overrideDnc = !!this.address()?.do_not_contact;
      const lineItems = this.buildLineItems();

      let interactionIdForAttachments: string;
      let leadId: string;

      if (this.isEditMode() && this.interactionId) {
        await this.interactionService.update({ id: this.interactionId, notes: this.detailsModel.notes });
        interactionIdForAttachments = this.interactionId;

        if (this.existingLeadId) {
          const lead = await this.leadService.update({
            id: this.existingLeadId,
            contact_name: this.contactModel.contact_name,
            contact_phone: this.contactModel.contact_phone,
            contact_email: this.contactModel.contact_email,
            preferred_contact_method: this.contactModel.preferred_contact_method,
            line_items: lineItems,
            currency: this.interestedModel.currency,
            notes: this.detailsModel.notes,
          });
          leadId = lead.id;
        } else {
          const lead = await this.leadService.create({
            address_id: this.addressId,
            contact_name: this.contactModel.contact_name,
            contact_phone: this.contactModel.contact_phone,
            contact_email: this.contactModel.contact_email,
            preferred_contact_method: this.contactModel.preferred_contact_method,
            line_items: lineItems,
            currency: this.interestedModel.currency,
            notes: this.detailsModel.notes,
          });
          leadId = lead.id;
          await this.interactionService.update({ id: this.interactionId, lead_id: lead.id });
        }
      } else {
        const result = await this.interactionService.create({
          address_id: this.addressId,
          interaction_type: this.interactionType,
          outcome_kind: 'lead',
          notes: this.detailsModel.notes,
          override_do_not_contact: overrideDnc,
        });
        if (!result.success) {
          this.error.set(this.translate.translate(`interactions.form.error.${result.reason}`));
          return;
        }
        interactionIdForAttachments = result.interaction.id;
        if (this.tagIds.length > 0) {
          await this.tagService.saveFor(result.interaction.id, this.tagIds);
        }

        const lead = await this.leadService.create({
          address_id: this.addressId,
          contact_name: this.contactModel.contact_name,
          contact_phone: this.contactModel.contact_phone,
          contact_email: this.contactModel.contact_email,
          preferred_contact_method: this.contactModel.preferred_contact_method,
          line_items: lineItems,
          currency: this.interestedModel.currency,
          notes: this.detailsModel.notes,
        });
        leadId = lead.id;
        await this.interactionService.update({ id: result.interaction.id, lead_id: lead.id });
      }

      await this.attachmentService.saveFor('interaction', interactionIdForAttachments, this.detailsModel.photos ?? []);

      if (this.scheduleModel.on && this.scheduleModel.start) {
        const start = this.fromLocalInput(this.scheduleModel.start);
        if (start) {
          const end = this.fromLocalInput(this.scheduleModel.end) ?? start;
          const params = {
            lead_id: leadId,
            address_id: this.addressId,
            appointment_type: this.scheduleModel.appointment_type,
            scheduled_start: start,
            scheduled_end: end,
            notes: this.scheduleModel.notes,
          };
          if (this.existingAppointmentId) {
            await this.schedulingProvider.reschedule(this.existingAppointmentId, start, end);
          } else {
            await this.schedulingProvider.book(params);
          }
        }
      }

      if (this.isEditMode()) {
        this.router.navigate(['/leads', leadId]);
      } else {
        this.savedFinal.set(true);
        setTimeout(() => this.router.navigate(['/leads', leadId]), 900);
      }
    } catch (err: unknown) {
      this.log.error('Failed to save lead', err);
      this.error.set(err instanceof Error ? err.message : this.translate.translate('interactions.form.lead.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  /** epoch ms → "YYYY-MM-DDTHH:mm" in local time, for a datetime-local input. */
  private toLocalInput(ms: number | null): string {
    if (!ms) return '';
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private fromLocalInput(value: string): number | null {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
}
